import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  ENTIDADES_CATALOG,
  RoomSchema,
  type Procedure,
  type UpdateProcedureInput,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { usePageTitle } from '@/shared/store/page';
import { ApiClientError } from '@/shared/api/client';
import { useUsersList } from '@/features/users/api';
import { useProcedureById, useUpdateProcedure } from './api';

type Feedback =
  | { kind: 'idle' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; message: string };

const ROOM_OPTIONS = RoomSchema.options;

/**
 * Editar datos básicos del episodio: fecha, hora, sala, anestesiólogo, entidad y
 * observaciones. El tipo, el paciente y el médico tratante son inmutables tras
 * crear (los define `UpdateProcedureSchema` en contracts). El estado se cambia
 * por la máquina de transición, no aquí.
 */
export function ProcedureEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: procedure, isLoading, isError, error, refetch } = useProcedureById(id);

  usePageTitle('Editar episodio', 'Fecha, hora, sala, profesionales y observaciones');

  if (isLoading) {
    return (
      <div className="max-w-[920px] mx-auto">
        <Card className="p-6" aria-busy="true">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 rounded bg-surface-alt animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (isError || procedure === undefined) {
    return (
      <div className="max-w-[920px] mx-auto">
        <Card>
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft text-danger mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="text-text font-bold text-base mb-1">
              No fue posible cargar el episodio
            </div>
            <p className="text-text-muted text-sm mb-4">
              {error?.message ?? 'No encontramos el procedimiento solicitado.'}
            </p>
            <div className="flex justify-center gap-2">
              {isError && (
                <Button variant="secondary" onClick={() => void refetch()}>
                  <RefreshCw className="w-4 h-4" /> Reintentar
                </Button>
              )}
              <Button variant="secondary" asChild>
                <Link to="/procedimientos">
                  <ArrowLeft className="w-4 h-4" /> Volver al listado
                </Link>
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return <ProcedureEditForm procedure={procedure} />;
}

function ProcedureEditForm({ procedure }: { procedure: Procedure }): JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateProcedure();
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });

  const anestesiologos = useMemo(() => {
    const items = usersQuery.data?.items ?? [];
    const roles: string[] = ['anestesiologo'];
    return items.filter((u) => roles.includes(u.rol) && u.estado === 'activo');
  }, [usersQuery.data]);

  const [fecha, setFecha] = useState<string>(procedure.fecha);
  const [hora, setHora] = useState<string>(procedure.hora);
  const [sala, setSala] = useState<string>(procedure.sala ?? '');
  const [anestesiologoId, setAnestesiologoId] = useState<string>(procedure.anestesiologoId ?? '');
  const [entidad, setEntidad] = useState<string>(procedure.entidad ?? '');
  const [observaciones, setObservaciones] = useState<string>(procedure.observaciones ?? '');
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'idle' });

  const backTo = `/procedimientos/${procedure.id}`;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFeedback({ kind: 'idle' });
    const body: UpdateProcedureInput = {
      fecha,
      hora,
      sala: sala === '' ? null : (sala as UpdateProcedureInput['sala']),
      anestesiologoId:
        anestesiologoId === '' ? null : (anestesiologoId as UpdateProcedureInput['anestesiologoId']),
      entidad: entidad.trim() === '' ? null : entidad.trim(),
      observaciones: observaciones.trim() === '' ? null : observaciones.trim(),
    };
    try {
      await update.mutateAsync({ id: procedure.id, body });
      setFeedback({ kind: 'ok', message: 'Cambios guardados. Redirigiendo…' });
      setTimeout(() => navigate(backTo), 600);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401 || err.status === 403) {
          setFeedback({ kind: 'error', message: 'No tienes permiso para editar este episodio.' });
          return;
        }
        if (err.status === 404) {
          setFeedback({ kind: 'error', message: 'El episodio ya no existe.' });
          return;
        }
        if (err.status >= 400 && err.status < 500) {
          setFeedback({ kind: 'error', message: 'Datos inválidos. Revisa los campos.' });
          return;
        }
      }
      setFeedback({
        kind: 'error',
        message: 'No fue posible conectar con el servidor. Inténtalo de nuevo.',
      });
    }
  };

  return (
    <div className="max-w-[920px] mx-auto">
      <button
        type="button"
        onClick={() => navigate(backTo)}
        className="flex items-center gap-2 text-sm text-text-soft hover:text-text mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Volver al episodio
      </button>

      <form onSubmit={onSubmit}>
        <Card className="p-6 mb-4">
          <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
            <CardTitle>Editar datos básicos</CardTitle>
            <CardDescription>
              {procedure.tipo}
              {procedure.entidad !== undefined && procedure.entidad !== ''
                ? ` · ${procedure.entidad}`
                : ''}
            </CardDescription>
          </CardHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fecha">Fecha</Label>
              <Input id="fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hora">Hora</Label>
              <Input id="hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Sala</Label>
              <SearchableSelect
                value={sala}
                onChange={setSala}
                placeholder="— Sin sala —"
                clearable
                options={ROOM_OPTIONS.map((r) => ({ value: r, label: r }))}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Anestesiólogo</Label>
              <SearchableSelect
                value={anestesiologoId}
                onChange={setAnestesiologoId}
                placeholder="— Sin asignar —"
                clearable
                loading={usersQuery.isLoading}
                options={anestesiologos.map((u) => ({
                  value: u.id,
                  label:
                    u.registroProfesional !== undefined && u.registroProfesional !== ''
                      ? `${u.nombre} — ${u.registroProfesional}`
                      : u.nombre,
                  keywords: u.registroProfesional,
                }))}
              />
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label>Entidad</Label>
              <SearchableSelect
                value={entidad}
                onChange={setEntidad}
                placeholder="— Sin entidad —"
                clearable
                options={ENTIDADES_CATALOG.map((ent) => ({ value: ent, label: ent }))}
              />
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="observaciones">Observaciones</Label>
              <textarea
                id="observaciones"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows={4}
                maxLength={2000}
                className="w-full px-3 py-2 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
              />
            </div>
          </div>
        </Card>

        {feedback.kind === 'error' && (
          <div className="mb-4 rounded-md bg-danger-soft border border-red-200 text-red-800 text-sm p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{feedback.message}</span>
          </div>
        )}
        {feedback.kind === 'ok' && (
          <div className="mb-4 rounded-md bg-success-soft border border-green-200 text-green-800 text-sm p-3">
            {feedback.message}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate(backTo)}>
            Cancelar
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ProcedureEditPage;
